import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getAllowances, AllowanceBaseModel } from "@/features/allowance";
import editAllowance from "@/features/allowance/api/editAllowance";
import AddAllowanceButton from "@/features/allowance/components/addAllowance/AddAllowanceButton";
import AllowanceTable from "@/features/allowance/components/table/AllowanceTable";
import { OverviewItemsModel } from "@/shared/components/baselineOverview/model/baselineOverviewModels";
import useProjectWBSContext from "@/shared/context/projectWBS/useProjectWBSContext";

type AllowanceTableProps = {
  allowancesData: AllowanceBaseModel[];
  overviewItems: OverviewItemsModel[];
};

export default function AllowanceBaselineView({
  allowancesData,
  overviewItems,
}: AllowanceTableProps) {
  const [allowances, setAllowances] = useState<AllowanceBaseModel[]>([]);
  const { outcome: wbsEntryId } = useParams();

  const { refetchProjectWBS } = useProjectWBSContext();

  useEffect(() => {
    setAllowances(allowancesData);
  }, [allowancesData]);

  const handleSaveCell = async (
    allowanceToUpdate: AllowanceBaseModel,
    name: string,
    value: number | string
  ) => {
    const updatedAllowance = {
      ...allowanceToUpdate,
      [name]: value,
    };
    try {
      const AllowanceBaseline = await editAllowance(updatedAllowance);
      setAllowances((prevAllowances) =>
        prevAllowances.map((allowance) =>
          allowance.id === AllowanceBaseline.id ? AllowanceBaseline : allowance
        )
      );
      refetchProjectWBS();
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to save cell: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while saving cell");
      }
    }
  };

  const handleRefreshAllowance = async () => {
    try {
      if (!wbsEntryId) {
        throw new Error("Outcome is null");
      }
      const updatedAllowances = await getAllowances(wbsEntryId);
      refetchProjectWBS();
      setAllowances(updatedAllowances);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to refresh allowances: ${error.message}`);
      } else {
        throw new Error("An unknown error occurred while refreshing allowances");
      }
    }
  };

  return (
    <>
      <AddAllowanceButton refetchAllowance={() => void handleRefreshAllowance()} />
      <AllowanceTable
        allowances={allowances}
        handleSaveCell={(allowanceToUpdate, columnId, newValue) =>
          void handleSaveCell(allowanceToUpdate, columnId, newValue)
        }
        refetchAllowance={() => void handleRefreshAllowance()}
        overviewItems={overviewItems}
      />
    </>
  );
}
